import { CLIENT_ID, ModulesType } from './client.model'
import { updateEventStructure } from './updateEventStructure.model'
import { Entrance, ENTRANCE_ID } from './entrance.model'
import { PASS_ID } from './pass.model'
import { Area } from './area.model'
import { STATION_ID } from './station.model'
import { Club } from './club.model'
import { Location } from './location.model'
import { StationType, PassSecurityLevel } from '../../app/shared/app.enums'
import { BookingAreaStats } from './booking.model'

export type EventStatus = 'running' | 'upcoming' | 'incomplete' | 'finished' | 'inactive'
export type EVENT_ID = string | number

export abstract class EventBase {
  id: EVENT_ID = NaN
  client_id: CLIENT_ID = NaN
  name: string = ''
  external_code: string = ''
  category: Array<number | string> = []
  password: string = ''
  parent_id: number = NaN
  opening_time: string = ''
  starting_time: string = ''
  closing_time: string = ''
  max_participants: number = NaN
  max_enforce: boolean = false
  restrictions: string = ''
  allow_transfer: boolean = true
  active: boolean = true
  image: string = ''
  keep_open: boolean = false
  location: Location
  time_limitations?: string = ''
  post_scan_transfer: boolean = false
  report_location: boolean = false
  secured?: PassSecurityLevel
}

export class Event extends EventBase {
  category_icon: string = ''
  update_entrances?: unknown
  clubs: Array<Club> = []
  club_ids: string = ''
  station_type? = StationType.front_camera
  passesLength?: number
  entrancesLength?: number
  categories: Array<EventType>
  event_statistics: EventStatistics
  areas: Array<Area> = []
  areas_statistics: AreasStatistics
  predefined_location_id?: number = NaN
  entrancesAmount?: number
  entrances_statistics: Array<Entrance> = []
}

export class EventStatistics {
  scannedPercent: number = NaN
  recentMeanScanRate: number = NaN
  totalScanned: number = NaN
  blockedFraud: number = NaN
  maxTickets: number = NaN
  peakPoint: number = NaN
  totalTickets: number = NaN
  wrongEntranceName: string = ''
  eventMismatch: number = NaN
  wrongEntrance: number = NaN
  running: number = NaN
  faulted: number = NaN
  icon: string = ''
}

export type GaterixEventType = 'connection_error' | 'connection_success'
export class LogGaterix {
  station_id: STATION_ID = NaN
  start: string = ''
  end: string = ''
  name: string = ''
  description: string = ''
}

/* export class EventKpis {
  scannedPercent: number = NaN;
  recentMeanScanRate: number = NaN;
  totalScanned: number = NaN;
  blockedFraud: number = NaN;
  maxTickets: number = NaN;
  peakPoint: number = NaN;
  totalTickets: number = NaN;
  wrongEntranceName: string = '';
  eventMismatch: number = NaN;
  wrongEntrance: number = NaN;
  running: number = NaN;
  faulted: number = NaN;
  icon: string = '';
} */

export type ActionType = 'enter' | 'exit' | 'info' | 'new' | 'update' | 'wrong_entrance' | 'event_ended'

export class LogEvent {
  id: string | number = NaN
  event_id: EVENT_ID = NaN
  entrance_id: ENTRANCE_ID = NaN
  station_id: STATION_ID = NaN
  pass_id: PASS_ID = NaN
  time: string = ''
  result: string = ''
  action: ActionType = 'enter'
  scanned_value: string = ''
}
export class EventWithStatus extends Event {
  status: EventStatus = 'upcoming'
}

export type EVENTTYPE_ID = string | number

export class EventType {
  id: EVENTTYPE_ID
  name: string
  icon: string
  slug?: string
  module: ModulesType
  reports: unknown
}

export class CreateEventTypeInput {
  name: string = ''
  icon: string = ''
}

export class UpdateEventTypeInput {
  id: EVENTTYPE_ID = NaN
  name: number = NaN
  icon: string = ''
}

export class CreateEventInput extends EventBase {
  predefined_location_id: number = NaN
  set_predefined_location?: boolean = false
  club_ids?: string = ''
  lat: number = NaN
  lon: number = NaN
  station_type? = StationType.front_camera
  location_name?: string = ''
  location_display_name?: string = ''
  location_description?: string = ''
  location_address: string = ''
  entrances?: number = NaN
  tickets_url?: string = ''
  description: string = ''
  sub_events: EventBase[] = []
  override secured?: PassSecurityLevel
}

export class UpdateEventInput extends EventBase {
  predefined_location_id: number = NaN
  club_ids?: string | null = null
  station_type? = StationType.front_camera
  update_location: Location = {} as Location
  update_event_structure?: updateEventStructure[] = []
  tickets_url?: string = ''
  description: string = ''
  sub_events: EventBase[] = []
  override secured?: PassSecurityLevel
}

export class EventResponse extends EventBase {
  club_ids?: string = ''
  station_type? = StationType.front_camera
  description: string = ''
  /** YB-1669: set when a tickets_url excel was STAGED for review instead of written inline —
   *  the id of the awaiting_review pass-upload job (opens the review dialog). */
  pass_upload_job_id?: number | null = null
  override secured?: PassSecurityLevel
}

export class ResponseEvent extends UpdateEventInput {}

export class AreasStatistics {
  totalInside: number = NaN
  authorizedOverTime: number = NaN
  relatedInside?: number = NaN
  totalRelated?: number = NaN
  employeesInside: number = NaN
  totalEmployees: number = NaN
  authorizedInside?: number = NaN
  totalAuthorized?: number = NaN
  totalVehicles?: number = NaN
  vehiclesInside?: number = NaN
  totalHME?: number = NaN
  HMEInside?: number = NaN
  areas: DataByArea[] = []
}

export class DataByArea {
  id: number = NaN
  name: string = ''
  entrance_id: number = NaN
  inside: number = NaN
  total: number = NaN
  employeesInside: number = NaN
  totalEmployees: number = NaN
  totalVehicles?: number = NaN
  vehiclesInside?: number = NaN
  totalHME?: number = NaN
  HMEInside?: number = NaN
  overTime: number = NaN
  absent: number = NaN
  sub_event: SubSubEventData
  /**
   * YB-2066 room stats, attached in the BROWSER for bookable areas — never selected from PAPI.
   *
   * Deliberately left without a default, like `sub_event` above: this class doubles as the
   * `areas { … }` selection set via `Object.keys(new DataByArea())`, and an initialised property
   * here would ask PAPI for a field `areas_statistics.areas` does not have and fail the whole
   * dashboard query.
   */
  booking?: BookingAreaStats | null
}

export class SubSubEventData {
  name: string = ''
  parent_name: string = ''
  start: Date = new Date()
  end: Date = new Date()
  total: number = NaN
  inside: number = NaN
}
